import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {getChangePlanCost, getTeamInviteContext, getTeamMembers, initChangePlanModalV2} from '@Panels/change-plan-modal-v2/change-plan-modal-v2-thunk';
import type {ChangePlanDialogV2Response, ChangePlanEntryStep, ChangePlanModalV2State, IAPChangePlanState, PromoEligiblePlans} from '@Panels/change-plan-modal-v2/change-plan-modal-v2.types';
import {ChangePlanStepV2, DEFAULT_CHANGE_PLAN_COST_V2, TeamStepVariant} from '@Panels/change-plan-modal-v2/change-plan-modal-v2.types';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import {CurrencyCode} from '@Utils/currency.util';
import {isIOSApp} from '@Utils/browser.util';

interface AppliedPromoCodePayload {
  code: string;
  discount: number;
  isOneTime: boolean;
  eligiblePlans: PromoEligiblePlans;
}

const initialState: ChangePlanModalV2State = {
  plans: [],
  isReady: false,
  isTrial: false,
  isPaused: false,
  isInactive: false,
  isCancelled: false,
  currentSeats: 1,
  currentSeatLimit: 1,
  promoCode: '',
  carriedPromoCode: '',
  promoEligiblePlans: {levels: [], months: []},
  isSubscriptionPromoRemoved: false,
  enteredPromoCode: '',
  enteredPromoDiscount: 0,
  enteredPromoIsOneTime: false,
  enteredPromoEligiblePlans: {levels: [], months: []},
  currentPlanId: 0,
  currentLevel: 0,
  currentDuration: 1,
  currentDisplayPrice: 0,
  selectedLevel: 0,
  selectedTierId: 0,
  selectedDuration: 1,
  currencyCode: CurrencyCode.USD,
  teamMembers: [],
  selectedSeats: 0,
  memberIdsToRemove: [],
  currentTierId: null,
  cancelPlanMessage: '',
  paymentMethodStr: '',
  manageCardsURL: '',
  iapProducts: {},
  isRenewalInvoicePending: false,
  showPricingIncreaseBanner: false,
  isTicketingAvailableInUserCountry: false,
  planLimits: {
    premiumMonthlyCredits: 0,
    premiumPlusMonthlyCredits: 0,
    premiumMonthlyEmailSends: 0,
    premiumPlusMonthlyEmailSends: 0,
    premiumSocialAccounts: 0,
    premiumEventPagesPerMonth: 0,
    premiumTicketsPerMonth: 0,
    premiumTicketRevenuePerMonth: 0,
    premiumTicketServiceFeePercent: 0,
    premiumPlusTicketServiceFeePercent: 0,
  },
  isFetchingTeamMembers: false,
  currentStep: ChangePlanStepV2.CHANGE_PLAN,
  stepHistory: [],
  transitionDirection: TransitionDirection.FORWARD,
  teamStepVariant: TeamStepVariant.INVITE,
  changePlanCost: DEFAULT_CHANGE_PLAN_COST_V2,
  inviteEmails: [],
  teamInviteContext: null,
};

export const changePlanModalV2Slice = createSlice({
  name: 'changePlanModalV2',
  initialState,
  reducers: {
    resetChangePlanModalV2: () => {
      return initialState;
    },
    setEntryStep: (state, {payload}: PayloadAction<ChangePlanEntryStep>) => {
      state.currentStep = payload;
      state.stepHistory = [];
      state.teamStepVariant = payload === ChangePlanStepV2.TEAM ? TeamStepVariant.GROW_WORKSPACE : TeamStepVariant.INVITE;
    },
    goToStep: (state, {payload}: PayloadAction<ChangePlanStepV2>) => {
      state.stepHistory.push(state.currentStep);
      state.currentStep = payload;
      state.transitionDirection = TransitionDirection.FORWARD;
    },
    goBackToPreviousStep: (state) => {
      const previousStep = state.stepHistory.pop();
      if (previousStep === undefined) {
        return;
      }
      state.currentStep = previousStep;
      state.transitionDirection = TransitionDirection.BACKWARD;
      if (previousStep !== ChangePlanStepV2.SUMMARY) {
        state.changePlanCost = DEFAULT_CHANGE_PLAN_COST_V2;
        // Dropping the code applies to the change that quote was for, so it is given back with the quote.
        // Leaving it set would let a reactivation — which cannot detach anything — inherit it and report a
        // removal it never performs.
        state.promoCode = state.carriedPromoCode;
        state.isSubscriptionPromoRemoved = false;
      }
    },
    updateSelectedTier: (state, {payload}: PayloadAction<number>) => {
      state.selectedTierId = payload;
    },
    updateSelectedLevel: (state, {payload}: PayloadAction<number>) => {
      state.selectedLevel = payload;
    },
    updateSelectedDuration: (state, {payload}: PayloadAction<number>) => {
      state.selectedDuration = payload;
    },
    updateSelectedSeats: (state, {payload}: PayloadAction<number>) => {
      state.selectedSeats = payload;
    },
    setInviteEmails: (state, {payload}: PayloadAction<string[]>) => {
      state.inviteEmails = payload;
    },
    applyEnteredPromoCode: (state, {payload}: PayloadAction<AppliedPromoCodePayload>) => {
      state.enteredPromoCode = payload.code;
      state.enteredPromoDiscount = payload.discount;
      state.enteredPromoIsOneTime = payload.isOneTime;
      state.enteredPromoEligiblePlans = payload.eligiblePlans;
    },
    clearEnteredPromoCode: (state) => {
      state.enteredPromoCode = '';
      state.enteredPromoDiscount = 0;
      state.enteredPromoIsOneTime = false;
      state.enteredPromoEligiblePlans = {levels: [], months: []};
    },
    /** Keeps a code on screen but drops its discount, for a selection it does not cover. */
    clearEnteredPromoDiscount: (state) => {
      state.enteredPromoDiscount = 0;
    },
    /**
     * Drops the code the subscription carries. Clearing it leaves the dialog in the state it has for a
     * subscriber who never had one, so the input comes back and a replacement can be entered and priced;
     * the flag is what tells the backend to detach the old code when the change is confirmed.
     */
    removeSubscriptionPromoCode: (state) => {
      state.promoCode = '';
      state.isSubscriptionPromoRemoved = true;
    },
    updateStateForIAP: (state, {payload}: PayloadAction<IAPChangePlanState>) => {
      state.plans = payload.plans;
      state.selectedLevel = payload.selectedLevel;
      state.selectedDuration = payload.selectedDuration;
      state.selectedSeats = payload.selectedSeats;
      state.selectedTierId = payload.selectedTierId;
      state.currentTierId = payload.currentTierId;
      state.iapProducts = payload.iapProducts;
      state.isReady = true;
    },
    selectMemberToRemove: (state, {payload}: PayloadAction<number>) => {
      if (!state.memberIdsToRemove.includes(payload)) {
        state.memberIdsToRemove.push(payload);
      }
    },
    deselectMemberToRemove: (state, {payload}: PayloadAction<number>) => {
      const index = state.memberIdsToRemove.indexOf(payload);
      if (index > -1) {
        state.memberIdsToRemove.splice(index, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initChangePlanModalV2.pending, (state) => {
      state.isReady = false;
    });

    builder.addCase(initChangePlanModalV2.fulfilled, (state, {payload}: PayloadAction<ChangePlanDialogV2Response>) => {
      state.isTrial = payload.isTrial;
      state.isPaused = payload.isPaused;
      state.isInactive = payload.isInactive;
      state.isCancelled = payload.isCancelled;
      state.promoCode = payload.promoCode;
      state.carriedPromoCode = payload.promoCode;
      state.promoEligiblePlans = payload.promoEligiblePlans;
      state.currencyCode = payload.currencyCode;
      state.plans = payload.plans;

      state.currentSeatLimit = payload.currentSeatLimit;
      state.currentSeats = payload.currentSeats;
      state.currentPlanId = payload.currentPlanId;
      state.currentLevel = payload.currentLevel;
      state.currentDuration = payload.currentDuration;
      state.currentDisplayPrice = payload.currentDisplayPrice;
      state.currentTierId = payload.currentTierId;
      state.cancelPlanMessage = payload.cancelPlanMessage;
      state.isRenewalInvoicePending = payload.isRenewalInvoicePending;
      state.showPricingIncreaseBanner = payload.showPricingIncreaseBanner;
      state.isTicketingAvailableInUserCountry = payload.isTicketingAvailableInUserCountry;
      state.planLimits = payload.planLimits;

      state.selectedLevel = payload.currentLevel;
      state.selectedTierId = payload.selectedTierId;
      state.selectedDuration = payload.selectedDuration;
      state.selectedSeats = payload.currentSeatLimit;

      if (Object.prototype.hasOwnProperty.call(payload, 'paymentMethodStr')) {
        state.paymentMethodStr = payload.paymentMethodStr;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'manageCardsURL')) {
        state.manageCardsURL = payload.manageCardsURL;
      }

      if (!isIOSApp()) {
        state.isReady = true;
      }
    });

    builder.addCase(getTeamMembers.pending, (state) => {
      state.isFetchingTeamMembers = true;
    });

    builder.addCase(getTeamMembers.fulfilled, (state, {payload}) => {
      state.teamMembers = Object.values(payload);
      state.isFetchingTeamMembers = false;
    });

    builder.addCase(getTeamMembers.rejected, (state) => {
      state.isFetchingTeamMembers = false;
    });

    builder.addCase(getTeamInviteContext.fulfilled, (state, {payload}) => {
      state.teamInviteContext = payload;
    });

    builder.addCase(getChangePlanCost.fulfilled, (state, {payload}) => {
      state.changePlanCost = payload;
    });
  },
});

export const {
  resetChangePlanModalV2,
  setEntryStep,
  goToStep,
  goBackToPreviousStep,
  updateSelectedTier,
  updateSelectedLevel,
  updateSelectedDuration,
  updateSelectedSeats,
  setInviteEmails,
  applyEnteredPromoCode,
  clearEnteredPromoCode,
  clearEnteredPromoDiscount,
  removeSubscriptionPromoCode,
  updateStateForIAP,
  selectMemberToRemove,
  deselectMemberToRemove,
} = changePlanModalV2Slice.actions;
export const changePlanModalV2Reducer = changePlanModalV2Slice.reducer;
